/**
 * Utility functions for handling URL search parameters in Next.js App Router
 */

/**
 * Converts URLSearchParams to a plain object compatible with Next.js Pages Router query format
 * Handles multiple values for the same key by converting them to arrays
 *
 * @param searchParams - URLSearchParams from useSearchParams()
 * @returns Plain object with string or string[] values
 */
export const searchParamsToObject = (
  searchParams: URLSearchParams
): Record<string, string | string[]> => {
  const obj: Record<string, string | string[]> = {}

  searchParams.forEach((value, key) => {
    const existing = obj[key]

    if (existing) {
      if (Array.isArray(existing)) {
        existing.push(value)
      } else {
        obj[key] = [existing, value]
      }
    } else {
      obj[key] = value
    }
  })

  return obj
}

/**
 * Creates an asPath equivalent for App Router by combining pathname with search params
 *
 * @param pathname - Current pathname from usePathname()
 * @param searchParams - URLSearchParams from useSearchParams()
 * @returns Full path with query string (equivalent to Pages Router asPath)
 */
export const createAsPath = (
  pathname: string | null,
  searchParams: URLSearchParams | null
): string => {
  return (
    (pathname || '') +
    (searchParams?.toString() ? `?${searchParams.toString()}` : '')
  )
}
